import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { Button as BaseUIButton } from "@base-ui/react/button";

type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

type AsyncClickHandler = (
  e: React.MouseEvent<HTMLButtonElement>,
) => void | Promise<void>;

interface ButtonProps extends Omit<
  NativeButtonProps,
  "disabled" | "className" | "onClick"
> {
  children: ReactNode;
  size?: "xs" | "sm" | "md";
  variant?: "primary" | "outline" | "ghost";
  disabled?: boolean;
  className?: string;
  onClick?: AsyncClickHandler;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      children,
      size = "md",
      variant = "primary",
      disabled = false,
      className = "",
      onClick,
      ...props // ← IMPORTANT: includes Base UI props
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading) return;

      setIsLoading(true);

      try {
        await onClick?.(e);
      } finally {
        setIsLoading(false);
      }
    };

    const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
      xs: "px-2 py-1 text-xs",
      sm: "px-3 py-1 text-sm",
      md: "px-4 py-2.5 text-lg",
    };

    // Variant Classes
    const variantClasses: Record<
      NonNullable<ButtonProps["variant"]>,
      string
    > = {
      primary: "bg-sky-600 hover:bg-sky-700 hover:cursor-pointer text-white",
      outline:
        "bg-blue text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
      ghost: "bg-transparent text-gray-600  hover:bg-gray-100",
    };

    return (
      <BaseUIButton
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        focusableWhenDisabled={true}
        onClick={handleClick}
        className={`inline-flex items-center justify-center gap-2 rounded transition cursor-pointer
          data-disabled:bg-gray-300 data-disabled:cursor-not-allowed
          ${sizeClasses[size]} ${variantClasses[variant]} ${className}
        `}
        {...props} // 🔑 Base UI handlers + aria go here
      >
        {children}
        {isLoading && (
          <span className="ml-2 inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
        )}
      </BaseUIButton>
    );
  },
);

export default Button;
